/**
 * Phase 20 Plan 20-02 (MOB-02): mobile navigation drawer.
 *
 * Wraps Radix Dialog with left-slide geometry (translateX(-100%) -> 0 via
 * drawerSlideIn from src/design/sheet-keyframes.css.ts). Mirrors the
 * staff + client nav-item branching from TopNav.tsx EXACTLY:
 *
 *   internal_staff: FLEET / SENTRIES / ALERTS / RECORDINGS / NOW
 *   client_user:    NOW (active) + TIMELINE / INSIGHTS / SITES placeholders
 *
 * Account section at the bottom:
 *   - User email (read-only, tertiary text)
 *   - Theme toggle (sun/moon, calls useTheme().toggleMode())
 *   - Sign-out button (calls useAuth().logout() + navigate('/login'))
 *
 * Active-route indicator: aria-current="page" + gold left-border on the
 * matching NavLink (CSS in MobileDrawer.css.ts via the
 * &[aria-current="page"] selector).
 *
 * Close behaviors:
 *   - Tap outside the panel (Radix Dialog native: Overlay click closes)
 *   - Esc key (Radix Dialog native: Escape closes)
 *   - Route change (useEffect on location.pathname: when pathname changes
 *     AND drawer is currently open, call onOpenChange(false))
 *   - Nav link click (each NavLink's onClick fires onOpenChange(false))
 *   - Sign-out button click (after logout completes)
 *
 * Used by TopNav.tsx: `<MobileDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />`
 * mounted alongside the existing TopNav. Radix Portal-renders the Overlay +
 * Content into document.body, so DOM position inside <TopNav> is fine.
 */
import { useCallback, useEffect, useRef } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip';
import { Moon, Sun } from '@phosphor-icons/react';
import { useAuth } from '@/features/auth/useAuth';
import { useTheme } from '@/design/ThemeProvider';
import {
  drawerOverlay,
  drawerContent,
  drawerNavList,
  drawerNavLink,
  drawerDivider,
  drawerAccount,
  drawerAccountEmail,
  drawerThemeToggle,
  drawerSignOutBtn,
} from './MobileDrawer.css';
import { tooltipContent } from '@/features/layout/TopNav.css';

/** Props for the MobileDrawer component. */
export interface MobileDrawerProps {
  /** Whether the drawer is currently open. Owned by the parent (TopNav). */
  open: boolean;
  /**
   * Callback fired when the drawer wants to change state. Triggered by:
   *   - Radix Dialog native dismissal (Esc, click outside, focus loss)
   *   - Nav-link click inside the drawer (onClick handler)
   *   - Sign-out button click (after logout resolves)
   *   - Route change (the location.pathname effect below)
   */
  onOpenChange: (next: boolean) => void;
}

/**
 * Active staff nav items - mirrors STAFF_NAV_ITEMS in TopNav.tsx exactly.
 *
 * 2026-06-10 (doc D issue 2): this list had drifted from TopNav - UPLOADS
 * was missing entirely, so phone users could not reach
 * /admin/mobile-sessions at all. Re-synced, and the new LOCATIONS + ORGS
 * entries (doc D issue 1) are mirrored here in the same TopNav order.
 */
const STAFF_NAV_ITEMS: { label: string; to: string }[] = [
  { label: 'FLEET', to: '/admin/fleet' },
  { label: 'SENTRIES', to: '/admin/sentries' },
  { label: 'ALERTS', to: '/admin/alerts' },
  { label: 'RECORDINGS', to: '/admin/recordings' },
  { label: 'UPLOADS', to: '/admin/mobile-sessions' },
  { label: 'LOCATIONS', to: '/admin/locations' },
  { label: 'ORGS', to: '/admin/orgs' },
  { label: 'NOW', to: '/now' },
];

/** Disabled placeholders for client_user - mirrors CLIENT_DISABLED_NAV_ITEMS. */
const CLIENT_DISABLED_NAV_ITEMS: { label: string; tooltip: string }[] = [
  { label: 'TIMELINE', tooltip: 'Timeline view (Phase 4)' },
  { label: 'INSIGHTS', tooltip: 'Insights analytics (Phase 5)' },
  { label: 'SITES', tooltip: 'Multi-site overview (Phase 4)' },
];

/**
 * Compute whether the given NavLink path matches the current location.
 * Mirrors the NavLink + `end` prop semantics from TopNav: the NOW link
 * uses end=true (so /now/foo doesn't match NOW), all admin links use
 * the default `startsWith` matching so nested admin routes still
 * activate the parent.
 */
function isActiveRoute(pathname: string, to: string): boolean {
  if (to === '/now') {
    return pathname === '/now';
  }
  return pathname === to || pathname.startsWith(to + '/');
}

/**
 * MobileDrawer - the slide-in left drawer surfaced at phone widths via
 * the TopNav hamburger button. See file header for the full behavior
 * contract.
 */
export function MobileDrawer({ open, onOpenChange }: MobileDrawerProps) {
  const { user, logout } = useAuth();
  const { mode, toggleMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isStaff = user?.actor_type === 'internal_staff' && !user?.impersonating_as;

  /**
   * Track the last seen pathname to detect actual route transitions.
   * Without this guard the effect would fire onOpenChange(false) on
   * the very first render (when the parent passes open=true and the
   * location is whatever route the user is on), prematurely closing
   * the drawer the instant it opens.
   */
  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (lastPathRef.current !== location.pathname) {
      lastPathRef.current = location.pathname;
      if (open) {
        onOpenChange(false);
      }
    }
  }, [location.pathname, open, onOpenChange]);

  /**
   * Handle nav-link click: close the drawer. React Router handles the
   * actual navigation via the NavLink href; this callback only fires
   * the onOpenChange(false) so the drawer dismisses.
   */
  const handleNavClick = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  /**
   * Handle the sign-out flow: call useAuth().logout() (clears tokens +
   * hits /v1/auth/logout), close the drawer, navigate to /login.
   * Mirrors the TopNav handleLogout pattern.
   */
  const handleSignOut = useCallback(async () => {
    await logout();
    onOpenChange(false);
    navigate('/login', { replace: true });
  }, [logout, navigate, onOpenChange]);

  /**
   * Handle the theme-toggle click: flip the theme via useTheme().toggleMode().
   * Drawer stays open so the user can see the immediate visual change.
   */
  const handleThemeToggle = useCallback(() => {
    toggleMode();
  }, [toggleMode]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={drawerOverlay} />
        <Dialog.Content
          className={drawerContent}
          aria-describedby={undefined}
        >
          {/*
            Visually-hidden Dialog.Title is required by Radix a11y contract.
            The visible heading inside the panel is the nav landmark name
            (aria-label="Mobile navigation"), so the title is empty for
            screen readers via the sr-only inline style.
          */}
          <Dialog.Title style={{
            position: 'absolute',
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0,
          }}>
            Mobile navigation
          </Dialog.Title>

          <TooltipProvider delayDuration={400}>
            <nav
              role="navigation"
              aria-label="Mobile navigation"
              className={drawerNavList}
            >
              {isStaff
                ? STAFF_NAV_ITEMS.map((item) => {
                    const active = isActiveRoute(location.pathname, item.to);
                    return (
                      <NavLink
                        key={item.label}
                        to={item.to}
                        end={item.to === '/now'}
                        className={drawerNavLink}
                        aria-current={active ? 'page' : undefined}
                        onClick={handleNavClick}
                        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                      >
                        {item.label}
                      </NavLink>
                    );
                  })
                : (
                  <>
                    {/* Active NOW for client users */}
                    <NavLink
                      to="/now"
                      end
                      className={drawerNavLink}
                      aria-current={location.pathname === '/now' ? 'page' : undefined}
                      onClick={handleNavClick}
                      data-testid="mobile-nav-now"
                    >
                      NOW
                    </NavLink>
                    {/* Disabled placeholders wrapped in Radix Tooltip. */}
                    {CLIENT_DISABLED_NAV_ITEMS.map((item) => (
                      <Tooltip key={item.label}>
                        <TooltipTrigger asChild>
                          <span
                            className={drawerNavLink}
                            aria-disabled="true"
                            role="link"
                            tabIndex={-1}
                            data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                            style={{ opacity: 0.5, cursor: 'not-allowed' }}
                          >
                            {item.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent
                          side="right"
                          sideOffset={8}
                          className={tooltipContent}
                        >
                          {item.tooltip}
                        </TooltipContent>
                      </Tooltip>
                    ))}
                  </>
                )}
            </nav>
          </TooltipProvider>

          <hr className={drawerDivider} />

          <div className={drawerAccount}>
            {/*
              User email row - reads from useAuth(). Empty when the
              AuthProvider has not yet resolved /v1/auth/me; the row
              renders nothing in that pre-auth window.
            */}
            {user?.email && (
              <div className={drawerAccountEmail} data-testid="mobile-drawer-email">
                {user.email}
              </div>
            )}

            {/* Theme toggle - matches the desktop TopNav theme-toggle button. */}
            <button
              type="button"
              className={drawerThemeToggle}
              onClick={handleThemeToggle}
              aria-label={`Switch to ${mode === 'dark' ? 'light' : 'dark'} mode`}
              data-testid="mobile-drawer-theme-toggle"
            >
              {mode === 'dark' ? (
                <Moon size={16} weight="fill" aria-hidden="true" />
              ) : (
                <Sun size={16} weight="fill" aria-hidden="true" />
              )}
              <span>{mode === 'dark' ? 'Light mode' : 'Dark mode'}</span>
            </button>

            {/* Sign out button - calls useAuth().logout() + navigate(/login). */}
            <button
              type="button"
              className={drawerSignOutBtn}
              onClick={() => {
                void handleSignOut();
              }}
              data-testid="mobile-drawer-sign-out"
            >
              Sign out
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
